import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Icon } from '@ds/core';
import { useAviso } from '@/comuns/toast';
import { explicarErroDoBrain, textoParaFala } from '@/data/brain';
import type {
    AnexoDoBrain, ArquivoGeradoDoBrain, ErroExplicado, IdiomaDoBrain, MotivoDaVozReserva, PropostaDoBrain, SituacaoDaProposta,
} from '@/data/brain';
import { dados } from '@/data/porta';
import { b, s } from './classes';
import { Dialogo, DialogoCabecalho, DialogoCorpo, DialogoRodape, Dica, Folha, Selo, estiloAncorado, useFecharPorFora, useRetanguloDaAncora } from './ui/camadas';

/* ================================================================ menu da conta */

export interface MenuDaContaProps {
    titulo: string;
    pessoa: string;
    clinica: string;
    escuro: boolean;
    aoVoltar: () => void;
}

/**
 * `BrainOrgMenu`: o título com o gradiente do Brain e o painel com a pessoa. No
 * original o painel mostrava plano e créditos; aqui, a clínica da sessão.
 */
export function MenuDaConta({ titulo, pessoa, clinica, escuro, aoVoltar }: MenuDaContaProps) {
    const [aberto, setAberto] = useState(false);
    const gatilho = useRef<HTMLButtonElement>(null);
    const painel = useRef<HTMLDivElement>(null);
    const rect = useRetanguloDaAncora(gatilho.current, aberto);
    useFecharPorFora(aberto, (alvo) => Boolean(painel.current?.contains(alvo) || gatilho.current?.contains(alvo)), () => setAberto(false), gatilho.current);
    useEffect(() => {
        if (aberto) setTimeout(() => painel.current?.querySelector<HTMLElement>('button')?.focus(), 0);
    }, [aberto]);

    return (
        <div className="brain-org-menu">
            <button
                ref={gatilho} type="button" className="brain-org-menu__trigger" aria-expanded={aberto} aria-haspopup="menu"
                onClick={(e) => { e.stopPropagation(); setAberto((v) => !v); }}
            >
                <span className={b.brandGradient}>{titulo}</span>
                <span className={`brain-org-menu__chevron${aberto ? ' brain-org-menu__chevron--open' : ''}`} style={{ display: 'inline-flex' }}><Icon name="chevron-down" size={12} /></span>
            </button>
            {aberto && rect && createPortal(
                <div
                    ref={painel} role="menu" aria-label={`Conta: ${pessoa}`}
                    className={`brain-org-menu__panel brain-org-menu__panel--portal brain-org-menu__panel--open${escuro ? ' brain-org-menu__panel--dark' : ''}`}
                    style={estiloAncorado(rect, { minWidth: 232, offset: 4 })}
                >
                    <button type="button" role="menuitem" className="brain-org-menu__back" onClick={() => { setAberto(false); aoVoltar(); }}>
                        <Icon name="chevron-left" size={12} />
                        Início da clínica
                    </button>
                    <div className="brain-org-menu__identity">
                        <span className="brain-org-menu__avatar" aria-hidden="true">{(pessoa || 'B').charAt(0).toUpperCase()}</span>
                        <div className="brain-org-menu__identity-text">
                            <div className="brain-org-menu__identity-row">
                                <span className="brain-org-menu__org-name">{pessoa}</span>
                                {clinica && <span className="brain-org-menu__plan-badge">{clinica}</span>}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}

/* ================================================================ ouvir */

type EstadoDaLeitura = 'parado' | 'carregando' | 'tocando';

/**
 * O aviso de voz reserva sai UMA vez por sessão, e só quando há o que fazer a respeito.
 *
 * Aqui, ao contrário do modo de voz, não há lugar na tela para uma nota permanente: seria
 * um toast a cada resposta ouvida. Cota do dia e chave faltando pedem ação de quem
 * administra; limite por minuto passa sozinho e não vale interromper ninguém.
 */
let jaAvisouDaVozReserva = false;

const AVISO_DA_VOZ_RESERVA: Partial<Record<MotivoDaVozReserva, string>> = {
    'cota-do-dia': 'A cota de hoje da voz Gacrux (Gemini) acabou: as respostas saem na voz reserva até a virada.',
    'sem-chave': 'Não há chave do Gemini ligada na plataforma: as respostas saem na voz reserva da OpenAI.',
};

/** Ouvir uma resposta. O mesmo botão para: tocar e parar são uma coisa só para quem usa teclado. */
export function BotaoOuvir({ texto, idioma }: { texto: string; idioma: IdiomaDoBrain }) {
    const aviso = useAviso();
    const [estado, setEstado] = useState<EstadoDaLeitura>('parado');
    const audio = useRef<HTMLAudioElement | null>(null);
    const endereco = useRef<string | null>(null);
    /* Cada clique abre um pedido; a resposta de um pedido velho não toca. */
    const pedido = useRef(0);

    const soltar = useCallback(() => {
        audio.current?.pause();
        audio.current = null;
        if (endereco.current) URL.revokeObjectURL(endereco.current);
        endereco.current = null;
    }, []);

    useEffect(() => () => {
        pedido.current += 1;
        soltar();
    }, [soltar]);

    async function alternar() {
        pedido.current += 1;
        if (estado !== 'parado') {
            soltar();
            setEstado('parado');
            return;
        }
        const este = pedido.current;
        setEstado('carregando');
        try {
            const { audio: som, reserva } = await dados().brainFalar(textoParaFala(texto), { idioma });
            if (este !== pedido.current) return;
            const explicacao = reserva ? AVISO_DA_VOZ_RESERVA[reserva] : undefined;
            if (explicacao && !jaAvisouDaVozReserva) {
                jaAvisouDaVozReserva = true;
                aviso({ tone: 'info', title: 'Ouvindo a voz reserva', description: explicacao });
            }
            const url = URL.createObjectURL(som);
            endereco.current = url;
            const tocador = new Audio(url);
            audio.current = tocador;
            tocador.onended = () => {
                soltar();
                setEstado('parado');
            };
            await tocador.play();
            if (este === pedido.current) setEstado('tocando');
        } catch (e) {
            if (este !== pedido.current) return;
            soltar();
            setEstado('parado');
            aviso({ tone: 'danger', title: 'Não foi possível ouvir a resposta', description: explicarErroDoBrain(e).detalhe });
        }
    }

    const rotulo = estado === 'tocando' ? 'Parar a leitura em voz alta' : estado === 'carregando' ? 'Cancelar a leitura em voz alta' : 'Ouvir a resposta';
    const icone = estado === 'tocando' ? 'square' : estado === 'carregando' ? 'loader-circle' : 'volume-2';
    return (
        <Dica titulo={rotulo}>
            <button type="button" className={b.messageActionBtn} aria-label={rotulo} aria-pressed={estado !== 'parado'} onClick={() => { void alternar(); }}>
                <Icon name={icone} size={11} />
            </button>
        </Dica>
    );
}

/* ================================================================ ação proposta */

const ICONE_DA_SITUACAO: Record<SituacaoDaProposta, string> = {
    pendente: 'hand',
    confirmada: 'circle-check',
    cancelada: 'ban',
    falhou: 'circle-alert',
    expirada: 'timer-off',
};

const ROTULO_DA_SITUACAO: Record<SituacaoDaProposta, string> = {
    pendente: 'Aguardando sua confirmação',
    confirmada: 'Confirmada',
    cancelada: 'Cancelada',
    falhou: 'Não foi feita',
    expirada: 'Expirada',
};

function desfecho(p: PropostaDoBrain): ReactNode {
    switch (p.situacao) {
        case 'confirmada':
            return <p className="brain-acao__desfecho brain-acao__desfecho--ok">Feito. {p.resultado ?? 'A ação foi executada.'}</p>;
        case 'falhou':
            return <p className="brain-acao__desfecho brain-acao__desfecho--falha">Não foi feito: {p.motivo ?? 'a ação falhou no servidor.'}</p>;
        case 'expirada':
            return <p className="brain-acao__desfecho">Expirou sem confirmação — a proposta vale 30 minutos. Nada foi alterado; peça de novo se ainda quiser.</p>;
        case 'cancelada':
            return <p className="brain-acao__desfecho">Cancelada. Nada foi alterado.</p>;
        case 'pendente':
            return null;
    }
}

export interface CartaoDeAcaoProps {
    proposta: PropostaDoBrain;
    /** Motivo de não poder agir (impersonação). Presente, não há botão. */
    somenteLeitura: string | null;
    aoMudar: (proposta: PropostaDoBrain) => void;
}

/**
 * Uma ação proposta pelo Brain (regra 8), no visual do Brain original. Todos os
 * campos que o servidor mandou, exatamente como vieram — é deles que a pessoa
 * dá o aceite. Confirmar é sempre um clique; nunca por atalho nem ao abrir.
 */
export function CartaoDeAcao({ proposta, somenteLeitura, aoMudar }: CartaoDeAcaoProps) {
    const [agindo, setAgindo] = useState<'confirmar' | 'cancelar' | null>(null);
    const [erro, setErro] = useState<ErroExplicado | null>(null);

    async function agir(acao: 'confirmar' | 'cancelar') {
        setAgindo(acao);
        setErro(null);
        try {
            aoMudar(acao === 'confirmar' ? await dados().brainConfirmarProposta(proposta.id) : await dados().brainCancelarProposta(proposta.id));
        } catch (e) {
            setErro(explicarErroDoBrain(e));
        } finally {
            setAgindo(null);
        }
    }

    const pendente = proposta.situacao === 'pendente';
    return (
        <section className={`brain-acao brain-acao--${proposta.situacao}`} aria-label={`Ação proposta: ${proposta.resumo || 'ação'}`}>
            <div className="brain-acao__topo">
                <span className="brain-acao__icone"><Icon name={ICONE_DA_SITUACAO[proposta.situacao]} size={14} /></span>
                <div className="brain-acao__titulos">
                    <strong className="brain-acao__resumo">{proposta.resumo || 'Ação proposta'}</strong>
                    <span className="brain-acao__meta">Ferramenta: <code>{proposta.ferramenta}</code></span>
                </div>
                <Selo tamanho="sm">{agindo === 'confirmar' ? 'Executando…' : ROTULO_DA_SITUACAO[proposta.situacao]}</Selo>
            </div>
            {proposta.campos.length > 0 && (
                <dl className="brain-acao__campos">
                    {proposta.campos.map((c, i) => (
                        /* Rótulos podem se repetir; a ordem é a do servidor. */
                        <div key={`${c.rotulo}-${String(i)}`} className="brain-acao__campo">
                            <dt>{c.rotulo}</dt>
                            <dd>{c.valor}</dd>
                        </div>
                    ))}
                </dl>
            )}
            {pendente && somenteLeitura && <p className="brain-acao__desfecho">{somenteLeitura}</p>}
            {pendente && !somenteLeitura && (
                <div className="brain-acao__botoes">
                    <button type="button" className={s.btnPrimary} disabled={agindo !== null} aria-label={`Confirmar: ${proposta.resumo}`} onClick={() => { void agir('confirmar'); }}>
                        <Icon name={agindo === 'confirmar' ? 'loader-circle' : 'check'} size={14} />
                        Confirmar
                    </button>
                    <button type="button" className={s.btnGhost} disabled={agindo !== null} aria-label={`Cancelar: ${proposta.resumo}`} onClick={() => { void agir('cancelar'); }}>
                        <Icon name="x" size={14} />
                        Cancelar
                    </button>
                </div>
            )}
            {desfecho(proposta)}
            {erro && <p role="alert" className="brain-acao__desfecho brain-acao__desfecho--falha">{erro.titulo}. {erro.detalhe}</p>}
        </section>
    );
}

/* ================================================================ arquivos */

type TipoDoArquivo = 'json' | 'planilha' | 'csv' | 'pdf' | 'imagem' | 'texto' | 'outro';

export function tipoDoArquivo(a: { nome: string; tipo: string }): TipoDoArquivo {
    const mime = a.tipo.toLowerCase();
    const nome = a.nome.toLowerCase();
    if (mime.includes('json') || nome.endsWith('.json')) return 'json';
    if (mime.includes('csv') || nome.endsWith('.csv')) return 'csv';
    if (/sheet|excel/.test(mime) || /\.xlsx?$/.test(nome)) return 'planilha';
    if (mime === 'application/pdf' || nome.endsWith('.pdf')) return 'pdf';
    if (mime.startsWith('image/')) return 'imagem';
    if (mime.startsWith('text/') || /\.(txt|md)$/.test(nome)) return 'texto';
    return 'outro';
}

export function iconeDoArquivo(tipo: TipoDoArquivo): string {
    switch (tipo) {
        case 'pdf': return 'file-text';
        case 'planilha': case 'csv': return 'file-spreadsheet';
        case 'json': return 'file-json';
        case 'imagem': return 'image';
        default: return 'file';
    }
}

/** CSV em linhas e colunas; o separador é o que aparece mais na primeira linha. */
function tabelaDoCsv(texto: string): string[][] {
    const linhas = texto.replace(/\r/g, '').split('\n').filter((l) => l.trim());
    const primeira = linhas[0] ?? '';
    const separador = (primeira.match(/;/g)?.length ?? 0) >= (primeira.match(/,/g)?.length ?? 0) ? ';' : ',';
    return linhas.slice(0, 200).map((l) => l.split(separador).map((c) => c.replace(/^"|"$/g, '')));
}

export interface FolhaDoArquivoProps {
    arquivo: ArquivoGeradoDoBrain | null;
    aoFechar: () => void;
}

/** A gaveta à direita com a prévia do arquivo que o Brain gerou. */
export function FolhaDoArquivo({ arquivo, aoFechar }: FolhaDoArquivoProps) {
    const [url, setUrl] = useState<string | null>(null);
    const [conteudo, setConteudo] = useState<string | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const tipo = arquivo ? tipoDoArquivo(arquivo) : 'outro';

    useEffect(() => {
        setUrl(null);
        setConteudo(null);
        setErro(null);
        if (!arquivo) return undefined;
        let vivo = true;
        dados().brainAbrirArquivo(arquivo.url)
            .then(async (u) => {
                if (!vivo) return;
                setUrl(u);
                const t = tipoDoArquivo(arquivo);
                if (t === 'json' || t === 'csv' || t === 'texto') {
                    const r = await fetch(u);
                    const texto = await r.text();
                    if (vivo) setConteudo(texto);
                }
            })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível abrir o arquivo.'); });
        return () => { vivo = false; };
    }, [arquivo]);

    const json = (() => {
        if (tipo !== 'json' || conteudo === null) return null;
        try {
            return JSON.stringify(JSON.parse(conteudo), null, 2);
        } catch {
            return conteudo;
        }
    })();

    return (
        <Folha aberta={arquivo !== null} aoFechar={aoFechar} rotulo={arquivo ? `Arquivo: ${arquivo.nome}` : 'Arquivo'} classe={b.fileModal}>
            <div className={b.fileModalHeader}>
                <div className={b.fileModalTitle}>
                    <Icon name={iconeDoArquivo(tipo)} size={14} />
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{arquivo?.nome ?? 'Arquivo'}</span>
                    <Selo tamanho="sm">{tipo === 'outro' ? 'arquivo' : tipo}</Selo>
                </div>
                <button type="button" className={b.iconBtnPlain} onClick={aoFechar} aria-label="Fechar a prévia"><Icon name="x" size={14} /></button>
            </div>
            <div className={b.fileModalContent}>
                {erro && <div className={b.fileModalPre}>{erro}</div>}
                {!erro && !url && <div className={b.fileModalPre}>Carregando…</div>}
                {!erro && url && tipo === 'json' && <pre className={b.fileModalPre}>{json ?? 'Carregando…'}</pre>}
                {!erro && url && tipo === 'texto' && <pre className={b.fileModalPre} style={{ whiteSpace: 'pre-wrap' }}>{conteudo ?? 'Carregando…'}</pre>}
                {!erro && url && tipo === 'csv' && conteudo !== null && (() => {
                    const [cabeca, ...corpo] = tabelaDoCsv(conteudo);
                    return (
                        <div style={{ overflowX: 'auto' }}>
                            <table className={b.fileModalTable}>
                                {cabeca && <thead><tr>{cabeca.map((c, i) => <th key={`h-${String(i)}`}>{c}</th>)}</tr></thead>}
                                <tbody>{corpo.map((l, i) => <tr key={`l-${String(i)}`}>{l.map((c, j) => <td key={`c-${String(j)}`}>{c}</td>)}</tr>)}</tbody>
                            </table>
                        </div>
                    );
                })()}
                {!erro && url && tipo === 'pdf' && (
                    <div style={{ padding: 8, height: 'min(72vh, 720px)' }}>
                        <iframe title={arquivo?.nome ?? 'PDF'} src={url} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 12 }} />
                    </div>
                )}
                {!erro && url && tipo === 'imagem' && (
                    <div style={{ textAlign: 'center', padding: 16 }}>
                        <img alt={arquivo?.nome ?? 'Imagem'} src={url} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 12 }} />
                    </div>
                )}
                {!erro && url && (tipo === 'planilha' || tipo === 'outro') && (
                    <div className={b.fileModalPre}>Pré-visualização indisponível para este formato. Use o botão de baixar ou abra em nova aba.</div>
                )}
            </div>
            <div className={b.fileModalFooter}>
                {url && (
                    <>
                        <a className={b.downloadBtn} style={{ marginRight: 8 }} href={url} target="_blank" rel="noopener noreferrer">Abrir em nova aba</a>
                        <a className={b.downloadBtn} href={url} download={arquivo?.nome}><Icon name="download" size={12} /> Baixar</a>
                    </>
                )}
            </div>
        </Folha>
    );
}

export interface BibliotecaDeAnexosProps {
    aberta: boolean;
    aoFechar: () => void;
    gerados: ArquivoGeradoDoBrain[];
    enviados: AnexoDoBrain[];
    aoAbrirGerado: (arquivo: ArquivoGeradoDoBrain) => void;
}

/** A gaveta "Biblioteca de Anexos": o que o Brain gerou e o que você enviou nesta conversa. */
export function BibliotecaDeAnexos({ aberta, aoFechar, gerados, enviados, aoAbrirGerado }: BibliotecaDeAnexosProps) {
    const aviso = useAviso();
    const abrirEnviado = async (a: AnexoDoBrain) => {
        try {
            window.open(await dados().brainAbrirArquivo(a.caminho), '_blank', 'noopener,noreferrer');
        } catch (e) {
            aviso({ tone: 'danger', title: 'Não foi possível abrir o anexo', description: e instanceof Error ? e.message : undefined });
        }
    };
    const total = gerados.length + enviados.length;
    return (
        <Folha aberta={aberta} aoFechar={aoFechar} rotulo="Biblioteca de anexos" classe={b.libraryModal}>
            <div className={b.fileModalHeader}>
                <div className={b.fileModalTitle}>
                    <Icon name="book-open" size={14} />
                    Biblioteca de Anexos
                    <Selo tamanho="sm">{String(total)}</Selo>
                </div>
                <button type="button" className={b.iconBtnPlain} onClick={aoFechar} aria-label="Fechar a biblioteca"><Icon name="x" size={14} /></button>
            </div>
            <div className={b.fileModalContent}>
                {total === 0 ? (
                    <div className={b.libraryEmpty}>
                        <span style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, opacity: 0.4 }}><Icon name="book-open" size={28} /></span>
                        <div>Nenhum anexo nesta conversa</div>
                    </div>
                ) : (
                    <>
                        {gerados.length > 0 && (
                            <>
                                <div className={b.librarySection}>Gerados pela VecchIA</div>
                                {gerados.map((a) => (
                                    <div key={a.id} className={b.libraryItem}>
                                        <button type="button" className="brain-biblioteca__abrir" onClick={() => aoAbrirGerado(a)} aria-label={`Ver ${a.nome}`}>
                                            <span className={b.libraryItemIcon}><Icon name={iconeDoArquivo(tipoDoArquivo(a))} size={14} /></span>
                                            <span className={b.libraryItemInfo}>
                                                <span className={b.libraryItemTitle}>{a.nome}</span>
                                                <span className={b.libraryItemMeta}>{tipoDoArquivo(a).toUpperCase()}</span>
                                            </span>
                                        </button>
                                    </div>
                                ))}
                            </>
                        )}
                        {enviados.length > 0 && (
                            <>
                                <div className={b.librarySection}>Enviados por você</div>
                                {enviados.map((a) => (
                                    <div key={a.caminho} className={b.libraryItem}>
                                        <button type="button" className="brain-biblioteca__abrir" onClick={() => { void abrirEnviado(a); }} aria-label={`Abrir ${a.nome}`}>
                                            <span className={b.libraryItemIcon}><Icon name="file-text" size={14} /></span>
                                            <span className={b.libraryItemInfo}>
                                                <span className={b.libraryItemTitle}>{a.nome}</span>
                                                <span className={b.libraryItemMeta}>{a.tipo || 'Anexo'}</span>
                                            </span>
                                        </button>
                                    </div>
                                ))}
                            </>
                        )}
                    </>
                )}
            </div>
        </Folha>
    );
}

/* ================================================================ ajuda e confirmação */

export function AjudaDoBrain({ aberta, aoFechar }: { aberta: boolean; aoFechar: () => void }) {
    const secoes: { titulo: string; itens: string[] }[] = [
        {
            titulo: 'O que a VecchIA faz',
            itens: [
                'Responde sobre a agenda, contatos, vendas, estoque, CRM e financeiro da clínica — com as suas permissões.',
                'Lê PDF, Word, planilha, XML de nota fiscal, texto e imagem anexados, e gera arquivos.',
                'Busca na web (Anthropic e Gemini) e nos documentos da base de conhecimento da clínica.',
            ],
        },
        {
            titulo: 'Ações só com a sua confirmação',
            itens: [
                'Quando a VecchIA propõe agendar, remarcar, criar tarefa ou mandar mensagem, aparece um cartão com todos os campos.',
                'Nada é feito sem o clique em Confirmar. A proposta vale 30 minutos.',
            ],
        },
        {
            titulo: 'Conversas e projetos',
            itens: [
                'Cada conversa é só sua e é apagada depois de 90 dias.',
                'Projetos agrupam conversas: a lista de recentes mostra só as do projeto ativo.',
                'Renomeie pelo lápis da lista; edite e reenvie uma mensagem pelo lápis dela.',
            ],
        },
        {
            titulo: 'Voz, modelo e personalização',
            itens: [
                'Conversa por voz: fale e ouça a resposta. Ditado: fale e o texto aparece no campo.',
                'O seletor mostra só os provedores com chave ligada na plataforma.',
                'Em Personalizar, ajuste tom, detalhe, instruções e habilidades. As regras da VecchIA vêm antes.',
            ],
        },
    ];
    return (
        <Dialogo aberto={aberta} aoFechar={aoFechar} rotulo="Ajuda da VecchIA">
            <DialogoCabecalho>
                <h2 className={s.dialogTitle}><Icon name="circle-help" size={18} />VecchIA — assistente generativa que pode fazer praticamente tudo para sua clínica</h2>
                <p className={s.dialogLead}>Converse, anexe, fale e peça ações nos módulos — que só acontecem com a sua confirmação.</p>
            </DialogoCabecalho>
            <DialogoCorpo>
                {secoes.map((sec) => (
                    <div key={sec.titulo} className={s.section}>
                        <div className={s.sectionTitle}>{sec.titulo}</div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.55, color: 'var(--brain-text)' }}>
                            {sec.itens.map((i) => <li key={i}>{i}</li>)}
                        </ul>
                    </div>
                ))}
            </DialogoCorpo>
            <DialogoRodape>
                <button type="button" className={s.btnPrimary} onClick={aoFechar}>Entendi</button>
            </DialogoRodape>
        </Dialogo>
    );
}

export interface ConfirmarNoBrainProps {
    aberto: boolean;
    titulo: string;
    descricao: string;
    rotuloDaAcao: string;
    aoConfirmar: () => void;
    aoCancelar: () => void;
}

/** Confirmação no visual do Brain (apagar conversa, arquivar projeto). */
export function ConfirmarNoBrain({ aberto, titulo, descricao, rotuloDaAcao, aoConfirmar, aoCancelar }: ConfirmarNoBrainProps) {
    return (
        <Dialogo aberto={aberto} aoFechar={aoCancelar} rotulo={titulo} tamanho="md">
            <DialogoCabecalho>
                <h2 className={s.dialogTitle}><Icon name="triangle-alert" size={18} />{titulo}</h2>
                <p className={s.dialogLead}>{descricao}</p>
            </DialogoCabecalho>
            <DialogoRodape>
                <button type="button" className={s.btnGhost} onClick={aoCancelar}>Cancelar</button>
                <button type="button" className={`${s.btnPrimary} brain-acao__perigo`} onClick={aoConfirmar}>{rotuloDaAcao}</button>
            </DialogoRodape>
        </Dialogo>
    );
}
